import { Cat, KINDS, civDef, civIndex } from '../../gameplay/registry';
import { computeStats, type GlobalStats, type KindStats, type TechStats } from '../../gameplay/stats';
import { ALL_TECHS } from '../../gameplay/registry';
import { C, RES_COUNT } from '../../gameplay/types';
import { Pathfinder } from '../../world/navigation/pathfinder';
import { HEIGHT_SCALE, Terrain, type GameMap } from '../../world/maps/mapTypes';
import { FX_ONE, FX_SHIFT, FX_HALF } from './fixed';
import { Rng } from './rng';
import type { FormationType, Stance } from './commands';

export const MAX_ENTITIES = 8192;
/** Mounted units and siege take two places on a ship. Only villagers shelter in buildings. */
const TWO_PLACES = C.Cavalry | C.MountedArcher | C.Siege | C.Elephant;
const SLOT_BITS = 13;
const SLOT_MASK = (1 << SLOT_BITS) - 1;

export const slotOf = (id: number): number => id & SLOT_MASK;

/** 1 for kinds units can walk through (trees), by kind index. */
const WALKABLE = Uint8Array.from(KINDS, (k) => (k.resource?.walkable ? 1 : 0));

export const enum Order {
  None = 0,
  Move = 1,
  AttackMove = 2,
  Attack = 3,
  Gather = 4,
  Build = 5,
  Patrol = 6,
  Guard = 7,
  Follow = 8,
  Repair = 9,
  /** Engineers commandeering an enemy building. */
  Capture = 10,
  /** Walking to a building or ship to go inside. */
  Garrison = 11,
  /** A transport ship sailing to land its cargo at ordX/ordY. */
  Unload = 12,
}

export const enum UState {
  Idle = 0,
  Walking = 1,
  Gathering = 2,
  Returning = 3,
  Building = 4,
  Attacking = 5,
  /** A monk tending the wounded around it. */
  Healing = 6,
}

export const enum StanceId {
  Aggressive = 0,
  Defensive = 1,
  Hold = 2,
  Passive = 3,
}
export const STANCE_IDS: Record<Stance, StanceId> = { aggressive: 0, defensive: 1, hold: 2, passive: 3 };

export const FORMATION_IDS: Record<FormationType, number> = { none: 0, line: 1, column: 2, box: 3, wedge: 4, square: 5, loose: 6 };

export interface QueueItem {
  /** Unit kind index, or -1 for a technology. */
  kind: number;
  tech: number;
  progress: number;
  total: number;
}

export interface PlayerState {
  id: number;
  name: string;
  civ: number;
  team: number;
  color: number;
  isAI: boolean;
  /** Resources in fixed-point units. */
  res: number[];
  /** Total gathered per resource (fixed-point) for scoring. */
  gathered: number[];
  age: number;
  researched: Uint8Array;
  researching: Uint8Array;
  pop: number;
  popCap: number;
  defeated: boolean;
  resigned: boolean;
  autoReseed: boolean;
  /** Market prices (gold per 100) per resource index. */
  prices: number[];
  kills: number;
  losses: number;
  stats: KindStats[];
  techStats: TechStats[];
  global: GlobalStats;
  /** Number of built buildings per kind (for prerequisites). */
  buildingCounts: Int32Array;
  lastSeq: number;
  /** Entity id -> tick when found unreachable by this player's units. */
  unreachable: Map<number, number>;
}

export type SimEvent =
  | { e: 'death'; kind: number; owner: number; x: number; y: number; fx: number; fy: number; building: boolean }
  | { e: 'attack'; id: number; kind: number; owner: number; x: number; y: number; target: number }
  | { e: 'hit'; x: number; y: number; kind: number; dmg: number; owner: number; targetOwner: number; building: boolean; splash: boolean }
  | { e: 'projectile'; slot: number }
  | { e: 'spawn'; id: number; kind: number; owner: number }
  | { e: 'heal'; x: number; y: number }
  | { e: 'built'; id: number; kind: number; owner: number }
  | { e: 'research'; tech: number; owner: number }
  | { e: 'age'; owner: number; age: number }
  | { e: 'underAttack'; owner: number; x: number; y: number; building: boolean }
  | { e: 'popCapped'; owner: number }
  | { e: 'depleted'; owner: number; x: number; y: number; res: number }
  | { e: 'gatherTick'; owner: number; res: number; x: number; y: number }
  | { e: 'defeated'; owner: number }
  | { e: 'captured'; id: number; kind: number; from: number; to: number }
  | { e: 'captureStart'; id: number; kind: number; owner: number; by: number; x: number; y: number }
  | { e: 'victory'; team: number; reason: string }
  | { e: 'notice'; owner: number; text: string }
  | { e: 'saveRequested'; owner: number; name: string };

export interface ProjectilePool {
  alive: Uint8Array;
  owner: Int8Array;
  kind: Int16Array;
  sx: Int32Array;
  sy: Int32Array;
  tx: Int32Array;
  ty: Int32Array;
  target: Int32Array;
  t: Int32Array;
  total: Int32Array;
  hits: Uint8Array;
  count: number;
}
export const MAX_PROJECTILES = 2048;

function typedToB64(a: ArrayBufferView): string {
  const u8 = new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode(...u8.subarray(i, i + 0x8000));
  return btoa(s);
}

function b64ToTyped<T extends ArrayBufferView>(b64: string, into: T): T {
  const bin = atob(b64);
  const u8 = new Uint8Array(into.buffer, into.byteOffset, into.byteLength);
  if (bin.length !== u8.length) throw new Error('Save data size mismatch');
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return into;
}

const civDefKey = (i: number): string => civDef(i).key;

/** Shallow and deep water are navigable by ships. */
export const isWaterTerrain = (t: number): boolean => t === Terrain.Shallow || t === Terrain.Water;

export class World {
  readonly map: GameMap;
  readonly size: number;
  tick = 0;
  rng: Rng;
  players: PlayerState[] = [];
  events: SimEvent[] = [];
  winnerTeam = -1;
  gameOver = false;

  // ---- Entity SoA storage ----
  alive = new Uint8Array(MAX_ENTITIES);
  gen = new Uint16Array(MAX_ENTITIES);
  kind = new Int16Array(MAX_ENTITIES);
  cat = new Uint8Array(MAX_ENTITIES);
  owner = new Int8Array(MAX_ENTITIES);
  x = new Int32Array(MAX_ENTITIES);
  y = new Int32Array(MAX_ENTITIES);
  px = new Int32Array(MAX_ENTITIES);
  py = new Int32Array(MAX_ENTITIES);
  hp = new Int32Array(MAX_ENTITIES);
  maxHp = new Int32Array(MAX_ENTITIES);
  order = new Uint8Array(MAX_ENTITIES);
  state = new Uint8Array(MAX_ENTITIES);
  target = new Int32Array(MAX_ENTITIES);
  ordX = new Int32Array(MAX_ENTITIES);
  ordY = new Int32Array(MAX_ENTITIES);
  /** Final destination for attack-move / patrol orders. */
  goalX = new Int32Array(MAX_ENTITIES);
  goalY = new Int32Array(MAX_ENTITIES);
  homeX = new Int32Array(MAX_ENTITIES);
  /** Friendly entity being guarded/followed. */
  ally = new Int32Array(MAX_ENTITIES);
  homeY = new Int32Array(MAX_ENTITIES);
  cooldown = new Int32Array(MAX_ENTITIES);
  timer = new Int32Array(MAX_ENTITIES);
  carryRes = new Int8Array(MAX_ENTITIES);
  carryAmt = new Int32Array(MAX_ENTITIES);
  /** Resource kind last gathered (for auto-seeking a replacement). */
  lastResKind = new Int16Array(MAX_ENTITIES);
  /** Remaining resource amount (fixed-point) for resources and farms. */
  resAmt = new Int32Array(MAX_ENTITIES);
  /** Construction progress (milli-ticks) for buildings. */
  buildProg = new Int32Array(MAX_ENTITIES);
  built = new Uint8Array(MAX_ENTITIES);
  builders = new Uint16Array(MAX_ENTITIES);
  buildersNext = new Uint16Array(MAX_ENTITIES);
  /** Farm worker id (or -1). */
  worker = new Int32Array(MAX_ENTITIES);
  fdx = new Int16Array(MAX_ENTITIES);
  fdy = new Int16Array(MAX_ENTITIES);
  moving = new Uint8Array(MAX_ENTITIES);
  lastAttackTick = new Int32Array(MAX_ENTITIES);
  lastHurtTick = new Int32Array(MAX_ENTITIES);
  /** 1 while a newly trained unit walks to its rally point (it fights back if attacked). */
  rallying = new Uint8Array(MAX_ENTITIES);
  /** Healing carried over between ticks, in 1/1024 hit points (see healing.ts). */
  healAcc = new Uint16Array(MAX_ENTITIES);
  /** Buildings being commandeered: capturing player + 1 (0 = none), progress (like buildProg) and the
   * hit points the building had when the capture began. */
  captureBy = new Int8Array(MAX_ENTITIES);
  captureProg = new Int32Array(MAX_ENTITIES);
  captureHp = new Int32Array(MAX_ENTITIES);
  /** Who first put up a captured building (player + 1; 0 = its owner did): it keeps their architecture. */
  builtBy = new Int8Array(MAX_ENTITIES);
  /** Held units (Cat.Held): the id of the building or ship they are inside (0 = none). */
  inside = new Int32Array(MAX_ENTITIES);
  /** Buildings and ships: places taken by the units inside. */
  cargo = new Uint8Array(MAX_ENTITIES);
  /** What a unit was doing when it was sent inside (order, target, position), for Back to Work. */
  prevOrder = new Uint8Array(MAX_ENTITIES);
  prevTarget = new Int32Array(MAX_ENTITIES);
  prevX = new Int32Array(MAX_ENTITIES);
  prevY = new Int32Array(MAX_ENTITIES);
  stance = new Uint8Array(MAX_ENTITIES);
  formation = new Uint8Array(MAX_ENTITIES);
  groupSpeed = new Int32Array(MAX_ENTITIES);
  stuck = new Uint16Array(MAX_ENTITIES);
  /** Make-way: where an idle unit stood before stepping aside for a friend, and ticks until it returns (0 = not yielding). */
  yieldX = new Int32Array(MAX_ENTITIES);
  yieldY = new Int32Array(MAX_ENTITIES);
  yieldTimer = new Uint16Array(MAX_ENTITIES);
  /** Footprint top-left tile for buildings. */
  btx = new Int16Array(MAX_ENTITIES);
  bty = new Int16Array(MAX_ENTITIES);
  rallyX = new Int32Array(MAX_ENTITIES);
  rallyY = new Int32Array(MAX_ENTITIES);
  rallyTarget = new Int32Array(MAX_ENTITIES);
  paths: (number[] | null)[] = new Array(MAX_ENTITIES).fill(null);
  pathIdx = new Int16Array(MAX_ENTITIES);
  /** Entity whose footprint is the path goal region (-1 = exact point). */
  destEnt = new Int32Array(MAX_ENTITIES);
  queues: (QueueItem[] | null)[] = new Array(MAX_ENTITIES).fill(null);
  /** Pending build orders after the current one (shift-queued). */
  buildQueue: (number[] | null)[] = new Array(MAX_ENTITIES).fill(null);

  private freeSlots: number[] = [];
  private nextSlot = 0;
  entityCount = 0;

  // ---- Grids ----
  /** Entity id occupying each tile (buildings/blocking resources), -1 if none. */
  occupant: Int32Array;
  /** 1 where land units cannot walk (terrain or occupant). Kept in sync with occupant. */
  blockGrid: Uint8Array;
  /** 1 where ships cannot sail (land or occupant). */
  blockGridWater: Uint8Array;
  pathfinderWater: Pathfinder;
  /** Per-slot: 1 if the unit is a ship (water navigation domain). */
  naval = new Uint8Array(MAX_ENTITIES);
  pathfinder: Pathfinder;
  /** Spatial hash for units. */
  readonly cellShift = 2; // 4-tile cells
  readonly cells: number;
  cellHead: Int32Array;
  cellNext = new Int32Array(MAX_ENTITIES);
  /** Static entities (buildings, resources) spatial hash, maintained incrementally. */
  staticCells: number[][];

  projectiles: ProjectilePool;
  /** Per-player visibility: 0 unexplored, 1 explored, 2 visible. */
  visibility: Uint8Array[] = [];
  /** Pending path requests (slots), processed with a per-tick budget. */
  pathQueue: number[] = [];
  /** Simulation rules version in force (see SIM_VERSION); set by the Simulation, not saved. */
  rules = 9;

  constructor(map: GameMap, seed: number) {
    this.map = map;
    this.size = map.size;
    this.rng = new Rng(seed);
    this.occupant = new Int32Array(map.size * map.size).fill(-1);
    this.blockGrid = new Uint8Array(map.size * map.size);
    for (let i = 0; i < this.blockGrid.length; i++) this.blockGrid[i] = map.passable[i] ? 0 : 1;
    this.pathfinder = new Pathfinder(map.size, this.blockGrid);
    this.blockGridWater = new Uint8Array(map.size * map.size);
    for (let i = 0; i < this.blockGridWater.length; i++) this.blockGridWater[i] = isWaterTerrain(map.terrain[i]) ? 0 : 1;
    this.pathfinderWater = new Pathfinder(map.size, this.blockGridWater);
    this.cells = Math.ceil(map.size / (1 << this.cellShift));
    this.cellHead = new Int32Array(this.cells * this.cells).fill(-1);
    this.staticCells = Array.from({ length: this.cells * this.cells }, () => []);
    this.worker.fill(-1);
    this.target.fill(-1);
    this.rallyTarget.fill(-1);
    this.projectiles = {
      alive: new Uint8Array(MAX_PROJECTILES),
      owner: new Int8Array(MAX_PROJECTILES),
      kind: new Int16Array(MAX_PROJECTILES),
      sx: new Int32Array(MAX_PROJECTILES),
      sy: new Int32Array(MAX_PROJECTILES),
      tx: new Int32Array(MAX_PROJECTILES),
      ty: new Int32Array(MAX_PROJECTILES),
      target: new Int32Array(MAX_PROJECTILES),
      t: new Int32Array(MAX_PROJECTILES),
      total: new Int32Array(MAX_PROJECTILES),
      hits: new Uint8Array(MAX_PROJECTILES),
      count: 0,
    };
  }

  // ---- Players ----
  addPlayer(opts: { name: string; civ: string; team: number; color: number; isAI: boolean; res: number[] }): PlayerState {
    const civ = civIndex(opts.civ);
    const researched = new Uint8Array(ALL_TECHS.length);
    const s = computeStats(civ, researched, this.rules);
    const p: PlayerState = {
      id: this.players.length, name: opts.name, civ, team: opts.team, color: opts.color, isAI: opts.isAI,
      res: opts.res.map((r) => r * FX_ONE), gathered: [0, 0, 0, 0], age: 0, researched,
      researching: new Uint8Array(ALL_TECHS.length), pop: 0, popCap: 0, defeated: false, resigned: false,
      autoReseed: true, prices: [100, 100, 100, 100], kills: 0, losses: 0,
      stats: s.kinds, techStats: s.techs, global: s.global, buildingCounts: new Int32Array(KINDS.length), lastSeq: -1, unreachable: new Map(),
    };
    this.players.push(p);
    return p;
  }

  recomputeStats(p: PlayerState): void {
    const s = computeStats(p.civ, p.researched, this.rules);
    p.stats = s.kinds;
    p.techStats = s.techs;
    p.global = s.global;
    // Apply HP changes to existing entities, preserving health fraction.
    for (let i = 0; i < this.nextSlot; i++) {
      if (!this.alive[i] || this.owner[i] !== p.id || this.cat[i] === Cat.Resource) continue;
      const nm = p.stats[this.kind[i]].hp;
      if (nm !== this.maxHp[i] && (this.cat[i] === Cat.Unit || this.cat[i] === Cat.Held || this.built[i])) {
        this.hp[i] = Math.max(1, Math.trunc((this.hp[i] * nm) / this.maxHp[i]));
        this.maxHp[i] = nm;
      }
    }
  }

  areEnemies(a: number, b: number): boolean {
    if (a < 0 || b < 0 || a === b) return false;
    return this.players[a].team !== this.players[b].team;
  }

  // ---- Entity lifecycle ----
  idOf(slot: number): number {
    return slot | (this.gen[slot] << SLOT_BITS);
  }

  /** Returns slot if id refers to a live entity, else -1. */
  resolve(id: number): number {
    if (id < 0) return -1;
    const s = id & SLOT_MASK;
    if (s >= MAX_ENTITIES || !this.alive[s] || this.gen[s] !== id >>> SLOT_BITS) return -1;
    return s;
  }

  get highSlot(): number {
    return this.nextSlot;
  }

  private allocSlot(): number {
    if (this.freeSlots.length > 0) return this.freeSlots.pop()!;
    if (this.nextSlot >= MAX_ENTITIES) return -1;
    return this.nextSlot++;
  }

  spawn(kindIdx: number, owner: number, x: number, y: number): number {
    const s = this.allocSlot();
    if (s < 0) return -1;
    const k = KINDS[kindIdx];
    this.alive[s] = 1;
    this.gen[s] = (this.gen[s] + 1) & 0xffff;
    this.kind[s] = kindIdx;
    this.cat[s] = k.cat;
    this.owner[s] = owner;
    this.x[s] = this.px[s] = x;
    this.y[s] = this.py[s] = y;
    this.order[s] = Order.None;
    this.state[s] = UState.Idle;
    this.target[s] = -1;
    this.ally[s] = -1;
    this.ordX[s] = x;
    this.ordY[s] = y;
    this.homeX[s] = x;
    this.homeY[s] = y;
    this.cooldown[s] = 0;
    this.timer[s] = 0;
    this.carryRes[s] = -1;
    this.carryAmt[s] = 0;
    this.lastResKind[s] = -1;
    this.resAmt[s] = 0;
    this.buildProg[s] = 0;
    this.built[s] = 1;
    this.builders[s] = 0;
    this.buildersNext[s] = 0;
    this.worker[s] = -1;
    this.fdx[s] = 0;
    this.fdy[s] = 1000;
    this.moving[s] = 0;
    this.lastAttackTick[s] = -1000;
    this.lastHurtTick[s] = -1000;
    this.rallying[s] = 0;
    this.healAcc[s] = 0;
    this.captureBy[s] = 0;
    this.captureProg[s] = 0;
    this.captureHp[s] = 0;
    this.builtBy[s] = 0;
    this.inside[s] = 0;
    this.cargo[s] = 0;
    this.prevOrder[s] = Order.None;
    this.prevTarget[s] = -1;
    this.prevX[s] = x;
    this.prevY[s] = y;
    this.stance[s] = StanceId.Aggressive;
    this.formation[s] = 0;
    this.groupSpeed[s] = 0;
    this.stuck[s] = 0;
    this.yieldTimer[s] = 0;
    this.paths[s] = null;
    this.pathIdx[s] = 0;
    this.queues[s] = null;
    this.buildQueue[s] = null;
    this.rallyX[s] = x;
    this.rallyY[s] = y;
    this.rallyTarget[s] = -1;
    this.naval[s] = k.unit && k.unit.classes & (1 << 8) ? 1 : 0;
    if (k.cat === Cat.Unit) {
      const st = this.players[owner].stats[kindIdx];
      this.hp[s] = this.maxHp[s] = st.hp;
      this.stance[s] = k.unit!.classes & 1 ? StanceId.Passive : StanceId.Aggressive;
    } else if (k.cat === Cat.Building) {
      const st = this.players[owner].stats[kindIdx];
      this.maxHp[s] = st.hp;
      this.hp[s] = st.hp;
    } else {
      const r = k.resource!;
      this.hp[s] = this.maxHp[s] = 1;
      this.resAmt[s] = r.amount * FX_ONE;
    }
    this.entityCount++;
    return s;
  }

  /** Place a building with its footprint top-left at tile (tx,ty). */
  spawnBuilding(kindIdx: number, owner: number, tx: number, ty: number, complete: boolean): number {
    const b = KINDS[kindIdx].building!;
    const half = (b.size * FX_ONE) >> 1;
    const s = this.spawn(kindIdx, owner, (tx << FX_SHIFT) + half, (ty << FX_SHIFT) + half);
    if (s < 0) return -1;
    this.btx[s] = tx;
    this.bty[s] = ty;
    const isFarm = b.foodAmount !== undefined;
    if (!isFarm) {
      for (let y = ty; y < ty + b.size; y++) for (let x = tx; x < tx + b.size; x++) this.setOccupant(y * this.size + x, this.idOf(s));
    }
    this.addStatic(s);
    if (complete) {
      this.built[s] = 1;
      this.onBuildingComplete(s);
    } else {
      this.built[s] = 0;
      this.hp[s] = Math.max(1, Math.trunc(this.maxHp[s] / 20));
    }
    // Rally point defaults to just outside the front edge.
    this.rallyX[s] = this.x[s];
    this.rallyY[s] = this.y[s] + half + FX_ONE;
    return s;
  }

  private dropoffCache: (number[] | null)[] = [];

  /** Completed drop-off buildings owned by a player (cached, invalidated on change). */
  dropoffsOf(owner: number): number[] {
    let c = this.dropoffCache[owner];
    if (!c) {
      c = [];
      for (let i = 0; i < this.nextSlot; i++) {
        if (!this.alive[i] || this.cat[i] !== Cat.Building || this.owner[i] !== owner || !this.built[i]) continue;
        if (KINDS[this.kind[i]].building!.dropoff) c.push(i);
      }
      this.dropoffCache[owner] = c;
    }
    return c;
  }

  onBuildingComplete(s: number): void {
    this.dropoffCache[this.owner[s]] = null;
    const b = KINDS[this.kind[s]].building!;
    const p = this.players[this.owner[s]];
    p.buildingCounts[this.kind[s]]++;
    if (b.foodAmount !== undefined) this.resAmt[s] = (b.foodAmount + p.global.farmFood) * FX_ONE;
  }

  /**
   * Hands a finished building to another player where it stands (engineers' capture). Its training
   * queue is lost without refund, its rally point resets, and a farm's worker is released.
   */
  transferBuilding(s: number, to: number): void {
    const from = this.owner[s];
    if (from === to || this.cat[s] !== Cat.Building) return;
    if (this.cargo[s]) this.releaseCargo(s, this.rallyX[s], this.rallyY[s]);
    const q = this.queues[s];
    if (q) for (const it of q) if (it.tech >= 0) this.players[from].researching[it.tech] = 0;
    this.queues[s] = [];
    if (this.built[s]) this.players[from].buildingCounts[this.kind[s]]--;
    this.dropoffCache[from] = null;
    const wk = this.resolve(this.worker[s]);
    if (wk >= 0 && this.owner[wk] === from) this.worker[s] = -1;
    if (!this.builtBy[s]) this.builtBy[s] = from + 1;
    if (this.builtBy[s] === to + 1) this.builtBy[s] = 0; // back with the side that built it
    this.owner[s] = to;
    if (this.built[s]) this.onBuildingComplete(s);
    const b = KINDS[this.kind[s]].building!;
    this.rallyX[s] = this.x[s];
    this.rallyY[s] = this.y[s] + ((b.size * FX_ONE) >> 1) + FX_ONE;
    this.rallyTarget[s] = -1;
  }

  spawnResource(kindIdx: number, tx: number, ty: number): number {
    const s = this.spawn(kindIdx, -1, (tx << FX_SHIFT) + FX_HALF, (ty << FX_SHIFT) + FX_HALF);
    if (s < 0) return -1;
    this.btx[s] = tx;
    this.bty[s] = ty;
    if (KINDS[kindIdx].resource!.blocking) this.setOccupant(ty * this.size + tx, this.idOf(s));
    this.addStatic(s);
    return s;
  }

  /** Whether an entity was recently found unreachable by a player's units. */
  isUnreachable(player: number, id: number): boolean {
    const t = this.players[player]?.unreachable.get(id);
    if (t === undefined) return false;
    if (this.tick - t > 1200) {
      this.players[player].unreachable.delete(id);
      return false;
    }
    return true;
  }

  /** True where a tile holds something units walk through (a tree, under rules v6). */
  isForest(i: number): boolean {
    const id = this.occupant[i];
    return id >= 0 && this.rules >= 6 && WALKABLE[this.kind[id & SLOT_MASK]] === 1;
  }

  setOccupant(i: number, id: number): void {
    this.occupant[i] = id;
    // Trees (rules v6) occupy their tile for building but let units through.
    const walkable = id >= 0 && this.rules >= 6 && WALKABLE[this.kind[id & SLOT_MASK]] === 1;
    this.blockGrid[i] = (id >= 0 && !walkable) || this.map.passable[i] === 0 ? 1 : 0;
    this.blockGridWater[i] = id >= 0 || !isWaterTerrain(this.map.terrain[i]) ? 1 : 0;
  }

  /** Pathfinder for a unit's navigation domain. */
  pathfinderFor(s: number): Pathfinder {
    return this.naval[s] ? this.pathfinderWater : this.pathfinder;
  }

  /** Blocked test in a unit's navigation domain. */
  blockedFor(s: number, tx: number, ty: number): boolean {
    return this.naval[s] ? this.pathfinderWater.isBlockedTile(tx, ty) : this.pathfinder.isBlockedTile(tx, ty);
  }

  kill(s: number): void {
    if (!this.alive[s]) return;
    if (this.cargo[s]) {
      // A building's garrison steps out of the ruins; a ship's passengers go down with it.
      if (this.cat[s] === Cat.Building) this.releaseCargo(s, this.rallyX[s], this.rallyY[s]);
      for (const h of this.heldIn(s)) this.kill(h);
    }
    if (this.cat[s] === Cat.Held) {
      const c = this.resolve(this.inside[s]);
      if (c >= 0) this.cargo[c] = Math.max(0, this.cargo[c] - this.placesFor(s, c));
      this.inside[s] = 0;
      this.cat[s] = Cat.Unit;
    }
    const c = this.cat[s];
    const k = KINDS[this.kind[s]];
    const id = this.idOf(s);
    if (c !== Cat.Resource) {
      this.events.push({ e: 'death', kind: this.kind[s], owner: this.owner[s], x: this.x[s], y: this.y[s], fx: this.fdx[s], fy: this.fdy[s], building: c === Cat.Building });
    }
    if (c === Cat.Building) {
      const b = k.building!;
      for (let y = this.bty[s]; y < this.bty[s] + b.size; y++)
        for (let x = this.btx[s]; x < this.btx[s] + b.size; x++) {
          const i = y * this.size + x;
          if (this.occupant[i] === id) this.setOccupant(i, -1);
        }
      if (this.built[s]) this.players[this.owner[s]].buildingCounts[this.kind[s]]--;
      this.dropoffCache[this.owner[s]] = null;
      const q = this.queues[s];
      if (q) for (const it of q) if (it.tech >= 0) this.players[this.owner[s]].researching[it.tech] = 0;
      this.removeStatic(s);
    } else if (c === Cat.Resource) {
      const i = this.bty[s] * this.size + this.btx[s];
      if (this.occupant[i] === id) this.setOccupant(i, -1);
      this.removeStatic(s);
    }
    if (c === Cat.Unit && this.owner[s] >= 0) this.players[this.owner[s]].losses++;
    // Release farm.
    if (c === Cat.Unit) {
      const t = this.resolve(this.target[s]);
      if (t >= 0 && this.worker[t] === id) this.worker[t] = -1;
    }
    this.alive[s] = 0;
    this.paths[s] = null;
    this.queues[s] = null;
    this.buildQueue[s] = null;
    this.entityCount--;
    // LIFO reuse is deterministic: every peer frees slots in the same order.
    this.freeSlots.push(s);
  }

  // ---- Garrisons and transports ----
  /** Places a unit takes inside a container: 0 if it cannot go in at all. */
  placesFor(s: number, c: number): number {
    const u = KINDS[this.kind[s]].unit;
    if (!u || u.classes & C.Ship) return 0;
    const ck = KINDS[this.kind[c]];
    if (ck.building) return ck.building.garrison && u.classes & C.Villager ? 1 : 0;
    if (!ck.unit?.garrison) return 0;
    return u.classes & TWO_PLACES ? 2 : 1;
  }

  /** Room for units inside (0 for anything that holds none). */
  capacityOf(c: number): number {
    const k = KINDS[this.kind[c]];
    return (k.building?.garrison ?? k.unit?.garrison) ?? 0;
  }

  /** Held units inside container c, in slot order. */
  heldIn(c: number): number[] {
    const id = this.idOf(c);
    const out: number[] = [];
    for (let s = 0; s < this.nextSlot; s++) if (this.alive[s] && this.cat[s] === Cat.Held && this.inside[s] === id) out.push(s);
    return out;
  }

  /** Take unit s off the map into container c. */
  hold(s: number, c: number): void {
    this.cat[s] = Cat.Held;
    this.inside[s] = this.idOf(c);
    this.cargo[c] += this.placesFor(s, c);
    this.x[s] = this.px[s] = this.x[c];
    this.y[s] = this.py[s] = this.y[c];
    this.order[s] = Order.None;
    this.state[s] = UState.Idle;
    this.target[s] = -1;
    this.paths[s] = null;
    this.moving[s] = 0;
    this.rallying[s] = 0;
    this.yieldTimer[s] = 0;
    this.buildQueue[s] = null;
  }

  /**
   * Land every unit inside container c on open ground around it (nearest to towardX/Y first).
   * Returns the units that came out; any left inside had nowhere to stand.
   */
  releaseCargo(c: number, towardX: number, towardY: number): number[] {
    const held = this.heldIn(c);
    if (!held.length) {
      this.cargo[c] = 0;
      return held;
    }
    const spots = this.landingSpots(c, towardX, towardY, held.length);
    if (!spots.length) return [];
    const out: number[] = [];
    held.forEach((s, i) => {
      const [x, y] = spots[i % spots.length];
      // Several to a tile when the shore is short: spread them a little.
      const k = Math.trunc(i / spots.length);
      const off = k ? (((k * 37) % 7) - 3) * (FX_ONE >> 3) : 0;
      this.cat[s] = Cat.Unit;
      this.inside[s] = 0;
      this.x[s] = this.px[s] = x + off;
      this.y[s] = this.py[s] = y - off;
      this.order[s] = Order.None;
      this.state[s] = UState.Idle;
      this.ordX[s] = this.homeX[s] = this.x[s];
      this.ordY[s] = this.homeY[s] = this.y[s];
      out.push(s);
    });
    this.cargo[c] = 0;
    return out;
  }

  /**
   * Open land tiles around a building's footprint or a ship (up to 3 tiles out), nearest to
   * towardX/Y first, at most `n` of them. Fixed-point tile centres.
   */
  landingSpots(c: number, towardX: number, towardY: number, n: number): [number, number][] {
    const building = this.cat[c] === Cat.Building;
    const size = building ? KINDS[this.kind[c]].building!.size : 1;
    const tx0 = building ? this.btx[c] : this.x[c] >> FX_SHIFT;
    const ty0 = building ? this.bty[c] : this.y[c] >> FX_SHIFT;
    const found: { x: number; y: number; d: number }[] = [];
    for (let ring = 1; ring <= 3 && found.length < n; ring++) {
      for (let ty = ty0 - ring; ty < ty0 + size + ring; ty++)
        for (let tx = tx0 - ring; tx < tx0 + size + ring; tx++) {
          if (tx > tx0 - ring && tx < tx0 + size + ring - 1 && ty > ty0 - ring && ty < ty0 + size + ring - 1) continue;
          if (tx < 0 || ty < 0 || tx >= this.size || ty >= this.size || this.pathfinder.isBlockedTile(tx, ty)) continue;
          const x = (tx << FX_SHIFT) + FX_HALF;
          const y = (ty << FX_SHIFT) + FX_HALF;
          const dx = (x - towardX) >> 4;
          const dy = (y - towardY) >> 4;
          found.push({ x, y, d: ring * 1e9 + dx * dx + dy * dy });
        }
    }
    found.sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
    return found.slice(0, n).map((f) => [f.x, f.y]);
  }

  // ---- Static spatial index ----
  private cellIndex(x: number, y: number): number {
    const cx = Math.min(this.cells - 1, Math.max(0, x >> (FX_SHIFT + this.cellShift)));
    const cy = Math.min(this.cells - 1, Math.max(0, y >> (FX_SHIFT + this.cellShift)));
    return cy * this.cells + cx;
  }
  private addStatic(s: number): void {
    const list = this.staticCells[this.cellIndex(this.x[s], this.y[s])];
    list.push(s);
    list.sort((a, b) => a - b);
  }
  private removeStatic(s: number): void {
    const list = this.staticCells[this.cellIndex(this.x[s], this.y[s])];
    const i = list.indexOf(s);
    if (i >= 0) list.splice(i, 1);
  }

  /** Rebuild the unit spatial hash (called once per tick). */
  rebuildUnitGrid(): void {
    this.cellHead.fill(-1);
    // Iterate in descending slot order so lists are ascending (deterministic).
    for (let s = this.nextSlot - 1; s >= 0; s--) {
      if (!this.alive[s] || this.cat[s] !== Cat.Unit) continue;
      const c = this.cellIndex(this.x[s], this.y[s]);
      this.cellNext[s] = this.cellHead[c];
      this.cellHead[c] = s;
    }
  }

  /** Visit units within radius (fixed-point) of a point. Callback returns true to stop. */
  forUnitsInRadius(x: number, y: number, r: number, fn: (s: number, d2: number) => boolean | void): void {
    const shift = FX_SHIFT + this.cellShift;
    const x0 = Math.max(0, (x - r) >> shift);
    const x1 = Math.min(this.cells - 1, (x + r) >> shift);
    const y0 = Math.max(0, (y - r) >> shift);
    const y1 = Math.min(this.cells - 1, (y + r) >> shift);
    const r2 = r * r;
    for (let cy = y0; cy <= y1; cy++)
      for (let cx = x0; cx <= x1; cx++) {
        for (let s = this.cellHead[cy * this.cells + cx]; s >= 0; s = this.cellNext[s]) {
          const dx = this.x[s] - x;
          const dy = this.y[s] - y;
          const d2 = dx * dx + dy * dy;
          if (d2 <= r2 && fn(s, d2)) return;
        }
      }
  }

  /** Visit buildings/resources whose centre is within radius (+ margin for large footprints). */
  forStaticInRadius(x: number, y: number, r: number, fn: (s: number, d2: number) => boolean | void): void {
    const shift = FX_SHIFT + this.cellShift;
    const pad = 3 * FX_ONE;
    const x0 = Math.max(0, (x - r - pad) >> shift);
    const x1 = Math.min(this.cells - 1, (x + r + pad) >> shift);
    const y0 = Math.max(0, (y - r - pad) >> shift);
    const y1 = Math.min(this.cells - 1, (y + r + pad) >> shift);
    const r2 = (r + pad) * (r + pad);
    for (let cy = y0; cy <= y1; cy++)
      for (let cx = x0; cx <= x1; cx++) {
        for (const s of this.staticCells[cy * this.cells + cx]) {
          const dx = this.x[s] - x;
          const dy = this.y[s] - y;
          const d2 = dx * dx + dy * dy;
          if (d2 <= r2 && fn(s, d2)) return;
        }
      }
  }

  // ---- Geometry helpers ----
  /** Half footprint (fixed) of a building or collision radius of other entities. */
  extent(s: number): number {
    const k = KINDS[this.kind[s]];
    if (k.building) return (k.building.size * FX_ONE) >> 1;
    if (k.unit) return Math.round(k.unit.radius * FX_ONE);
    return Math.round(k.resource!.radius * FX_ONE);
  }

  /** Distance from point to entity edge (fixed-point). Buildings/resources use their footprint square. */
  edgeDist(s: number, x: number, y: number): number {
    const c = this.cat[s];
    if (c === Cat.Unit) {
      const dx = this.x[s] - x;
      const dy = this.y[s] - y;
      return Math.max(0, Math.floor(Math.sqrt(dx * dx + dy * dy)) - this.extent(s));
    }
    const half = c === Cat.Building ? this.extent(s) : FX_HALF;
    const dx = Math.max(0, Math.abs(x - this.x[s]) - half);
    const dy = Math.max(0, Math.abs(y - this.y[s]) - half);
    return Math.floor(Math.sqrt(dx * dx + dy * dy));
  }

  /** Height (in HEIGHT_SCALE units) at a fixed-point position, bilinear. Integer-only. */
  heightAt(x: number, y: number): number {
    const vs = this.size + 1;
    const tx = Math.min(this.size - 1, Math.max(0, x >> FX_SHIFT));
    const ty = Math.min(this.size - 1, Math.max(0, y >> FX_SHIFT));
    const fx = Math.min(FX_ONE, Math.max(0, x - (tx << FX_SHIFT)));
    const fy = Math.min(FX_ONE, Math.max(0, y - (ty << FX_SHIFT)));
    const h = this.map.heights;
    const a = h[ty * vs + tx];
    const b = h[ty * vs + tx + 1];
    const c = h[(ty + 1) * vs + tx];
    const d = h[(ty + 1) * vs + tx + 1];
    const top = a * (FX_ONE - fx) + b * fx;
    const bot = c * (FX_ONE - fx) + d * fx;
    return Math.trunc((top * (FX_ONE - fy) + bot * fy) / (FX_ONE * FX_ONE));
  }

  /** Inclusive tile rectangle covered by a building/resource, or null for units. */
  footprint(s: number): number[] | null {
    const c = this.cat[s];
    if (c === Cat.Unit) return null;
    const size = c === Cat.Building ? KINDS[this.kind[s]].building!.size : 1;
    return [this.btx[s], this.bty[s], this.btx[s] + size - 1, this.bty[s] + size - 1];
  }

  tileBlocked(tx: number, ty: number): boolean {
    return this.pathfinder.isBlockedTile(tx, ty);
  }

  isWaterTile(tx: number, ty: number): boolean {
    return this.map.terrain[ty * this.size + tx] === Terrain.Water;
  }

  /** Find an open tile adjacent to a building footprint closest to (x,y). */
  exitPoint(s: number, towardX: number, towardY: number, water = false): [number, number] {
    const size = KINDS[this.kind[s]].building?.size ?? 1;
    const tx0 = this.btx[s] - 1;
    const ty0 = this.bty[s] - 1;
    let best: [number, number] | null = null;
    let bestD = Infinity;
    for (let ring = 0; ring < 4 && !best; ring++) {
      const a = tx0 - ring;
      const b = ty0 - ring;
      const n = size + 2 + ring * 2;
      for (let j = 0; j < n; j++)
        for (let i = 0; i < n; i++) {
          if (i !== 0 && j !== 0 && i !== n - 1 && j !== n - 1) continue;
          const tx = a + i;
          const ty = b + j;
          if (water ? this.pathfinderWater.isBlockedTile(tx, ty) : this.tileBlocked(tx, ty)) continue;
          const cx = (tx << FX_SHIFT) + FX_HALF;
          const cy = (ty << FX_SHIFT) + FX_HALF;
          const d = (cx - towardX) * (cx - towardX) + (cy - towardY) * (cy - towardY);
          if (d < bestD) {
            bestD = d;
            best = [cx, cy];
          }
        }
    }
    return best ?? [this.x[s], this.y[s] + this.extent(s) + FX_HALF];
  }

  // ---------- Serialisation ----------
  private static readonly ARRAY_FIELDS = [
    'alive', 'gen', 'kind', 'cat', 'owner', 'x', 'y', 'px', 'py', 'hp', 'maxHp', 'order', 'state', 'target', 'ordX', 'ordY',
    'goalX', 'goalY', 'homeX', 'homeY', 'ally', 'cooldown', 'timer', 'carryRes', 'carryAmt', 'lastResKind', 'resAmt', 'buildProg',
    'built', 'builders', 'buildersNext', 'worker', 'fdx', 'fdy', 'moving', 'lastAttackTick', 'lastHurtTick', 'stance', 'formation',
    'groupSpeed', 'stuck', 'btx', 'bty', 'rallyX', 'rallyY', 'rallyTarget', 'pathIdx', 'destEnt', 'occupant', 'blockGrid', 'blockGridWater', 'naval', 'yieldX', 'yieldY', 'yieldTimer', 'rallying', 'healAcc',
    'captureBy', 'captureProg', 'captureHp', 'builtBy', 'inside', 'cargo', 'prevOrder', 'prevTarget', 'prevX', 'prevY',
  ] as const;

  serialize(): Record<string, unknown> {
    const arrays: Record<string, string> = {};
    for (const f of World.ARRAY_FIELDS) arrays[f] = typedToB64((this as unknown as Record<string, ArrayBufferView>)[f]);
    const pr = this.projectiles;
    return {
      tick: this.tick,
      rng: this.rng.state,
      winnerTeam: this.winnerTeam,
      gameOver: this.gameOver,
      nextSlot: this.nextSlot,
      freeSlots: this.freeSlots.slice(),
      entityCount: this.entityCount,
      arrays,
      paths: this.paths.slice(0, this.nextSlot),
      queues: this.queues.slice(0, this.nextSlot),
      buildQueue: this.buildQueue.slice(0, this.nextSlot),
      pathQueue: this.pathQueue.slice(),
      visibility: this.visibility.map(typedToB64),
      projectiles: {
        alive: typedToB64(pr.alive), owner: typedToB64(pr.owner), kind: typedToB64(pr.kind), sx: typedToB64(pr.sx), sy: typedToB64(pr.sy),
        tx: typedToB64(pr.tx), ty: typedToB64(pr.ty), target: typedToB64(pr.target), t: typedToB64(pr.t), total: typedToB64(pr.total),
        hits: typedToB64(pr.hits), count: pr.count,
      },
      players: this.players.map((p) => ({
        id: p.id, name: p.name, civ: p.civ, team: p.team, color: p.color, isAI: p.isAI, res: p.res, gathered: p.gathered, age: p.age,
        researched: Array.from(p.researched), researching: Array.from(p.researching), pop: p.pop, popCap: p.popCap,
        defeated: p.defeated, resigned: p.resigned, autoReseed: p.autoReseed, prices: p.prices, kills: p.kills, losses: p.losses,
        buildingCounts: Array.from(p.buildingCounts), lastSeq: p.lastSeq, unreachable: [...p.unreachable],
      })),
    };
  }

  deserialize(d: Record<string, unknown>): void {
    const o = d as ReturnType<World['serialize']> & Record<string, never>;
    const a = o.arrays as Record<string, string>;
    // Fields added after a save was written are simply left at zero.
    for (const f of World.ARRAY_FIELDS) if (a[f] !== undefined) b64ToTyped(a[f], (this as unknown as Record<string, ArrayBufferView>)[f]);
    this.tick = o.tick as number;
    this.rng.state = o.rng as number;
    this.winnerTeam = o.winnerTeam as number;
    this.gameOver = o.gameOver as boolean;
    this.nextSlot = o.nextSlot as number;
    this.freeSlots = (o.freeSlots as number[]).slice();
    this.entityCount = o.entityCount as number;
    this.paths = new Array(MAX_ENTITIES).fill(null);
    this.queues = new Array(MAX_ENTITIES).fill(null);
    this.buildQueue = new Array(MAX_ENTITIES).fill(null);
    (o.paths as (number[] | null)[]).forEach((v, i) => (this.paths[i] = v));
    (o.queues as (QueueItem[] | null)[]).forEach((v, i) => (this.queues[i] = v));
    (o.buildQueue as (number[] | null)[]).forEach((v, i) => (this.buildQueue[i] = v));
    this.pathQueue = (o.pathQueue as number[]).slice();
    // A save from before rules v6 recorded trees as blocking; walk them under the current rules.
    for (let i = 0; i < this.occupant.length; i++) this.blockGrid[i] = (this.occupant[i] >= 0 && !this.isForest(i)) || this.map.passable[i] === 0 ? 1 : 0;
    this.visibility = (o.visibility as string[]).map((v) => b64ToTyped(v, new Uint8Array(this.size * this.size)));
    const pr = o.projectiles as Record<string, string | number>;
    for (const k of ['alive', 'owner', 'kind', 'sx', 'sy', 'tx', 'ty', 'target', 't', 'total', 'hits'] as const) b64ToTyped(pr[k] as string, this.projectiles[k]);
    this.projectiles.count = pr.count as number;
    this.players = [];
    for (const sp of o.players as Record<string, unknown>[]) {
      const p = this.addPlayer({ name: sp.name as string, civ: civDefKey(sp.civ as number), team: sp.team as number, color: sp.color as number, isAI: sp.isAI as boolean, res: [0, 0, 0, 0] });
      p.res = (sp.res as number[]).slice();
      p.gathered = (sp.gathered as number[]).slice();
      p.age = sp.age as number;
      p.researched.set(sp.researched as number[]);
      p.researching.set(sp.researching as number[]);
      p.pop = sp.pop as number;
      p.popCap = sp.popCap as number;
      p.defeated = sp.defeated as boolean;
      p.resigned = sp.resigned as boolean;
      p.autoReseed = sp.autoReseed as boolean;
      p.prices = (sp.prices as number[]).slice();
      p.kills = sp.kills as number;
      p.losses = sp.losses as number;
      p.buildingCounts.set(sp.buildingCounts as number[]);
      p.lastSeq = sp.lastSeq as number;
      p.unreachable = new Map(sp.unreachable as [number, number][]);
      const s = computeStats(p.civ, p.researched, this.rules);
      p.stats = s.kinds;
      p.techStats = s.techs;
      p.global = s.global;
    }
    // Rebuild derived indices.
    for (const l of this.staticCells) l.length = 0;
    for (let s = 0; s < this.nextSlot; s++) if (this.alive[s] && (this.cat[s] === Cat.Building || this.cat[s] === Cat.Resource)) this.addStatic(s);
    this.dropoffCache = [];
    this.events = [];
    this.rebuildUnitGrid();
  }

  heightWorld(x: number, y: number): number {
    return this.heightAt(x, y) / HEIGHT_SCALE;
  }

  resourceTotals(p: PlayerState): number[] {
    return p.res.slice(0, RES_COUNT).map((r) => Math.floor(r / FX_ONE));
  }
}
